import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api from "@api";
import "./WebsiteSettings.css";

function slugify(input = "") {
  return input
    .trim()
    .toLowerCase()
    .replace(/&/g, "and")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "")
    .slice(0, 40);
}

function WebsiteSettings() {
  const navigate = useNavigate();
  const { id } = useParams();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [organizationName, setOrganizationName] = useState("");
  const [slug, setSlug] = useState("");
  const [slugTouched, setSlugTouched] = useState(false);
  const [slugStatus, setSlugStatus] = useState({
    checking: false,
    available: null,
    message: "",
  });
  const [domain, setDomain] = useState("");
  const [themeMode, setThemeMode] = useState("dark");
  const [primaryColor, setPrimaryColor] = useState("#111111");
  const [accentColor, setAccentColor] = useState("#d4af37");

  const [contactEmail, setContactEmail] = useState("");
  const [instagram, setInstagram] = useState("");
  const [facebook, setFacebook] = useState("");
  const [twitter, setTwitter] = useState("");
  const [youtube, setYoutube] = useState("");

  const [logoFile, setLogoFile] = useState(null);
  const [existingLogoUrl, setExistingLogoUrl] = useState("");
  const [removeLogo, setRemoveLogo] = useState(false);

  useEffect(() => {
    let alive = true;

    (async () => {
      try {
        const res = await api.get(`/api/onboarding/organizations/${id}`);
        if (!alive) return;

        const org = res.data?.organization;
        console.log(org)
        if (!org) return;

        setOrganizationName(org.name || "");
        setSlug(org.slug || "");
        setDomain(org.domain || "");
        setSlugTouched(true);
        setThemeMode(org.theme_mode || "dark");
        setPrimaryColor(org.primary_color || "#111111");
        setAccentColor(org.accent_color || "#111111");

        setContactEmail(org.contact_email || "");
        setInstagram(org.instagram_url || "");
        setFacebook(org.facebook_url || "");
        setTwitter(org.twitter_url || "");
        setYoutube(org.youtube_url || "");

        setExistingLogoUrl(org.logo_url || "");
      } catch (e) {
        console.error("Failed to load website settings", e);
      } finally {
        if (alive) setLoading(false);
      }
    })();

    return () => {
      alive = false;
    };
  }, [id]);

  useEffect(() => {
    if (!slugTouched) {
      setSlug(slugify(organizationName));
    }
  }, [organizationName, slugTouched]);

  useEffect(() => {
    let alive = true;
    const s = slug.trim();

    if (!s) {
      setSlugStatus({ checking: false, available: null, message: "" });
      return;
    }

    setSlugStatus((prev) => ({ ...prev, checking: true, message: "" }));

    const t = setTimeout(async () => {
      try {
        const res = await api.get("/api/onboarding/organizations/slug-check", {
          params: { slug: s, orgId: id || undefined },
        });

        if (!alive) return;

        const ok = Boolean(res.data?.available);

        setSlugStatus({
          checking: false,
          available: ok,
          message: ok ? "Available" : "Taken",
        });
      } catch (e) {
        if (!alive) return;

        setSlugStatus({
          checking: false,
          available: null,
          message: "Could not verify",
        });
      }
    }, 350);

    return () => {
      alive = false;
      clearTimeout(t);
    };
  }, [slug, id]);

  const publicUrl = useMemo(() => {
    if (domain.trim()) return domain.trim();
    if (slug.trim()) return `sportinghip.com/${slug.trim()}`;
    return "sportinghip.com/your-link";
  }, [domain, slug]);

  const resolvedLogoUrl = useMemo(() => {
    if (logoFile) return URL.createObjectURL(logoFile);
    return removeLogo ? "" : existingLogoUrl || "";
  }, [logoFile, existingLogoUrl, removeLogo]);

  const canSave =
    organizationName.trim().length >= 2 &&
    !saving;
  const handleSave = async () => {
    if (!canSave) return;

    setSaving(true);

    try {
      const formData = new FormData();
      formData.append("name", organizationName.trim());
      if (!domain.trim()) {
        formData.append("slug", slug.trim());
      }
      formData.append("theme_mode", themeMode);
      formData.append("primary_color", primaryColor.trim() || "#111111");
      formData.append("accent_color", accentColor.trim() || "");
      formData.append("contact_email", contactEmail.trim() || "");
      formData.append("instagram_url", instagram.trim() || "");
      formData.append("facebook_url", facebook.trim() || "");
      formData.append("twitter_url", twitter.trim() || "");
      formData.append("youtube_url", youtube.trim() || "");
      formData.append("remove_logo", removeLogo ? "true" : "false");

      if (logoFile) {
        formData.append("logo", logoFile);
      }

      await api.put(`/api/onboarding/organizations/${id}`, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      sessionStorage.setItem("dashboard_success_message", "Website settings updated.");
      navigate(`/dashboard/organizations/${id}`);
    } catch (e) {
      console.error("Failed to save website settings", e);
      alert("Could not save website settings.");
      setSaving(false);
    }
  };

  if (loading) return null;

  return (
    <div className="website-settings-page">
      <div className="website-settings-shell">
        <div className="website-settings-topbar">
          <button
            type="button"
            className="website-settings-back"
            onClick={() => navigate(`/dashboard/organizations/${id}`)}
          >
            Back
          </button>
        </div>

        <div className="website-settings-header">
          <div>
            <div className="website-settings-eyebrow">Website Settings</div>
            <h1 className="website-settings-heading">Manage your website</h1>
            <p className="website-settings-subtext">
              Update settings that apply across your website.
            </p>
          </div>

        </div>

        <div className="website-settings-grid">
          <div className="website-settings-main">
            <section className="website-settings-section">
              <div className="website-settings-section-head">
                <h2>Branding</h2>
                <p>Basic settings for how your site looks and is identified.</p>
              </div>

              <label className="website-settings-field">
                <span>Organization name</span>
                <input
                  value={organizationName}
                  onChange={(e) => setOrganizationName(e.target.value)}
                  placeholder="Fast Action Basketball"
                />
              </label>

              {!domain.trim() && (
                <>
                  <label className="website-settings-field">
                    <span>SportingHip link</span>
                    <div className="website-settings-slug-row">
                      <div className="website-settings-slug-prefix">sportinghip.com/</div>
                      <input
                        value={slug}
                        onChange={(e) => {
                          setSlugTouched(true);
                          setSlug(slugify(e.target.value));
                        }}
                        placeholder="fastactionbasketball"
                      />
                    </div>

                    <div className="website-settings-slug-status">
                      {slugStatus.checking ? (
                        <span className="website-settings-muted">Checking…</span>
                      ) : slugStatus.message ? (
                        <span
                          className={
                            slugStatus.available === false
                              ? "website-settings-bad"
                              : "website-settings-good"
                          }
                        >
                          {slugStatus.available === false ? "✖ " : "✓ "}
                          {slugStatus.message}
                        </span>
                      ) : (
                        <span className="website-settings-muted">
                          This is your public SportingHip website address.
                        </span>
                      )}
                    </div>
                  </label>
                </>
              )}
              <div className="website-settings-row-3">
                <label className="website-settings-field">
                  <span>Theme mode</span>
                  <select value={themeMode} onChange={(e) => setThemeMode(e.target.value)}>
                    <option value="dark">Dark</option>
                    <option value="light">Light</option>
                  </select>
                </label>

                <label className="website-settings-field">
                  <span>Primary color</span>
                  <div className="website-settings-color-inline">
                    <input
                      type="color"
                      value={primaryColor || "#111111"}
                      onChange={(e) => setPrimaryColor(e.target.value)}
                      className="website-settings-color-picker-circle"
                    />
                    <div className="website-settings-color-value">{primaryColor || "#111111"}</div>
                  </div>
                </label>

                <label className="website-settings-field">
                  <span>Accent color</span>
                  <div className="website-settings-color-inline">
                    <input
                      type="color"
                      value={accentColor || "#111111"}
                      onChange={(e) => setAccentColor(e.target.value)}
                      className="website-settings-color-picker-circle"
                    />
                    <div className="website-settings-color-value">{accentColor || "#111111"}</div>
                  </div>
                </label>
              </div>

              <div className="website-settings-field">
                <span>Logo</span>

                {resolvedLogoUrl ? (
                  <div className="website-settings-logo-preview">
                    <img src={resolvedLogoUrl} alt="Logo preview" />
                  </div>
                ) : null}

                {existingLogoUrl && !logoFile && !removeLogo ? (
                  <div className="website-settings-existing-file-row">
                    <span className="website-settings-existing-file-pill">Current logo uploaded</span>
                    <a
                      href={existingLogoUrl}
                      target="_blank"
                      rel="noreferrer"
                      className="website-settings-existing-file-link"
                    >
                      View
                    </a>
                    <button
                      type="button"
                      className="website-settings-existing-file-remove"
                      onClick={() => {
                        setRemoveLogo(true);
                        setLogoFile(null);
                      }}
                    >
                      Remove
                    </button>
                  </div>
                ) : null}

                {removeLogo ? (
                  <div className="website-settings-existing-file-row">
                    <span className="website-settings-existing-file-pill">Logo will be removed</span>
                    <button
                      type="button"
                      className="website-settings-existing-file-link"
                      onClick={() => setRemoveLogo(false)}
                    >
                      Undo
                    </button>
                  </div>
                ) : null}

                <div className="website-settings-file-row">
                  <input
                    id="website-logo-upload"
                    type="file"
                    accept="image/*"
                    className="website-settings-file-hidden"
                    onChange={(e) => {
                      const file = e.target.files?.[0] || null;
                      setLogoFile(file);
                      if (file) setRemoveLogo(false);
                    }}
                  />
                  <label htmlFor="website-logo-upload" className="website-settings-file-btn">
                    Choose File
                  </label>
                  <div className="website-settings-file-name">
                    {logoFile?.name || "No file chosen"}
                  </div>
                </div>
              </div>
            </section>

            <section className="website-settings-section">
              <div className="website-settings-section-head">
                <h2>Contact & Socials</h2>
                <p>These can be used across your website.</p>
              </div>

              <label className="website-settings-field">
                <span>Contact email</span>
                <input
                  value={contactEmail}
                  onChange={(e) => setContactEmail(e.target.value)}
                  placeholder="hello@example.com"
                />
              </label>

              <div className="website-settings-row-2">
                <label className="website-settings-field">
                  <span>Instagram</span>
                  <input
                    value={instagram}
                    onChange={(e) => setInstagram(e.target.value)}
                    placeholder="https://instagram.com/..."
                  />
                </label>

                <label className="website-settings-field">
                  <span>Facebook</span>
                  <input
                    value={facebook}
                    onChange={(e) => setFacebook(e.target.value)}
                    placeholder="https://facebook.com/..."
                  />
                </label>
              </div>

              <div className="website-settings-row-2">
                <label className="website-settings-field">
                  <span>Twitter / X</span>
                  <input
                    value={twitter}
                    onChange={(e) => setTwitter(e.target.value)}
                    placeholder="https://x.com/..."
                  />
                </label>

                <label className="website-settings-field">
                  <span>YouTube</span>
                  <input
                    value={youtube}
                    onChange={(e) => setYoutube(e.target.value)}
                    placeholder="https://youtube.com/..."
                  />
                </label>
              </div>
            </section>
          </div>

          <aside className="website-settings-sidebar">
            <div className="website-settings-sidecard">
              <div className="website-settings-sidecard-title">Current settings</div>

              <div className="website-settings-summary-list">
                <div className="website-settings-summary-item">
                  <span>Name</span>
                  <strong>{organizationName || "—"}</strong>
                </div>

                <div className="website-settings-summary-item">
                  <span>Public address</span>
                  <strong>{publicUrl}</strong>

                  <div className="website-settings-muted" style={{ marginTop: 6 }}>
                    {domain.trim()
                      ? "Contact SportingHip to make domain changes."
                      : "Want to add a custom domain? Contact SportingHip to get set up."}
                  </div>
                </div>

                <div className="website-settings-summary-item">
                  <span>Email</span>
                  <strong>{contactEmail || "—"}</strong>
                </div>
              </div>
            </div>
          </aside>
        </div>

        <div className="website-settings-footer">
          <button
            type="button"
            className="website-settings-btn website-settings-btn-ghost"
            onClick={() => navigate(`/dashboard/organizations/${id}`)}
          >
            Cancel
          </button>

          <button
            type="button"
            className="website-settings-btn website-settings-btn-primary"
            onClick={handleSave}
            disabled={!canSave}
          >
            {saving ? "Saving..." : "Save Changes"}
          </button>
        </div>
      </div>
    </div>
  );
}

export default WebsiteSettings;